import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { UserService } from "./user.service";
import { MapBoxService } from "./mapbox.service";
import { OperationService } from "./operation.service";
import { AbsenceCalendar } from "../_models/absence.model";
import { PATimeControl } from "../_components/planning-assistant/singletons/pa-time-control";
import type { GridTechnicianInput, TwoWeekGridData } from "../welcome/capacity-bars/two-week-grid.util";
import { gridWorkdays } from "../welcome/capacity-bars/two-week-grid.util";
import { DistanceCalculator } from "../_components/planning-assistant/classes/location";
import { StoreCollection, TechnicianTour } from "../_models/technician-tour.model";
import type { WPTechnician } from "../_models/technician.interface";

// Max. Wartezeit auf Absence.io-Daten: meldet sich Absence.io nicht (der
// 500er im Backend kommt teils erst nach einem langen Timeout), wird die
// Kapazitätsansicht ohne Abwesenheits-Kennzeichnungen geladen statt gar
// nicht
const ABSENCE_LOAD_TIMEOUT_MS = 8000;

// Lädt Auslastungsdaten (Prozent je Techniker und Arbeitstag) für die
// Kapazitätsansichten (2-Wochen-Grid im Dashboard, Kapazitätsplanungs-Seite).
//
// Die Berechnung läuft über dieselbe Touren-Pipeline wie die Tages-Balken
// (TechnicianTour je Techniker und Arbeitstag): OSRM-Fahrzeiten über den
// DistanceCalculator, Auftragszeiten aus finished−on_site bzw. Schätzung,
// + 30 min Pause, relativ zur Tageskapazität (8h Arbeit + 0:30h Pause).
// Die Prozentwerte sind damit IDENTISCH zu den Auslastungs-Badges der Balken.
@Injectable({
  providedIn: 'root',
})
export class CapacityDataService {

  readonly distanceCalculator: DistanceCalculator
  readonly storeCollection = new StoreCollection()

  constructor(
    private userService: UserService,
    private absenceCalendar: AbsenceCalendar,
    mapBoxService: MapBoxService,
    operationService: OperationService,
  ) {
    this.distanceCalculator = new DistanceCalculator(mapBoxService, operationService)
  }

  // Montag der Woche des Datums (lokal)
  startOfWeek(date: Date): Date {
    const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7)
    return monday
  }

  // Lädt je Techniker die Auslastung in Prozent je Arbeitstag über `weeks`
  // Wochen ab Montag — exakt über die Touren-Berechnung der Tages-Balken.
  // Je Tag wird ein WP-Hash-Bulk-Call geladen (Tages-Operationen inkl.
  // Startkoordinaten) und je Techniker eine TechnicianTour gerechnet.
  async loadGridDataFromTours(
    start_monday: Date,
    weeks: number,
    technicians: GridTechnicianInput[],
    _now?: Date,
  ): Promise<TwoWeekGridData> {
    const days = gridWorkdays(start_monday, weeks)

    // Abwesenheiten einmal für den gesamten Zeitraum nachladen (Grundlage
    // für Urlaub/Krank/Feiertag/keine Schicht — wie bei den Balken).
    // skip_loaded_dates=false: Der Zeitraum wird immer frisch geladen —
    // sonst würde ein nachträglich eingetragener Urlaub (z. B. KW wurde
    // vorher schon einmal angesehen) dauerhaft nicht erscheinen
    const range_end = days[days.length - 1]
    // Abwesenheits-Daten zeitlich begrenzt laden: Hängt Absence.io, kommt
    // der 500er erst nach einem Backend-Timeout — die Kapazität soll dann
    // trotzdem angezeigt werden (nur die Abwesenheits-Kennzeichnungen
    // fehlen). Bei funktionierendem Absence.io ist der Call schnell fertig
    await Promise.race([
      this.absenceCalendar.loadAbsencesBetweenDates(
        PATimeControl.Instance.dateToDatestring(start_monday, true, false, '-'),
        PATimeControl.Instance.dateToDatestring(range_end, true, false, '-'),
        false
      ),
      new Promise<void>(resolve => setTimeout(resolve, ABSENCE_LOAD_TIMEOUT_MS)),
    ])

    const result = technicians.map(tech => {
      if (!tech.absence_user_id) {
        // Diagnose: Ohne Absence.io-Verknüpfung bleiben Urlaub/Krank unsichtbar —
        // Ursache ist meist eine abweichende E-Mail bzw. ein abweichender Name
        console.warn(`[Kapazität] Kein Absence.io-Benutzer für "${tech.name}" gefunden — Abwesenheiten können nicht angezeigt werden`)
      }
      return {
        name: tech.name,
        values: [] as (number | null)[],
        titles: [] as string[],
        absences: [] as (string | null)[],
      }
    })

    for (let day_index = 0; day_index < days.length; day_index++) {
      const day = days[day_index]
      const day_ts = PATimeControl.Instance.dateToTimestamp(day, true, false)
      const hashes_by_id = await this.loadDayHashes(day)

      await Promise.all(technicians.map(async (technician, tech_index) => {
        const entry = result[tech_index]
        const absence_reason = this.getDayAbsenceReason(technician, day)

        // Abwesend (Urlaub/Krank/Feiertag/keine Schicht): wie bei den Balken
        // kein Prozentwert, sondern der Abwesenheitsgrund
        if (absence_reason) {
          entry.values.push(null)
          entry.absences.push(absence_reason)
          entry.titles.push(absence_reason)
          return
        }
        entry.absences.push(null)

        const hash = hashes_by_id.get(technician.id)
        if (!hash) {
          // Kein WP-Hash (z.B. inaktiv) — keine Daten statt 0 %
          entry.values.push(null)
          entry.titles.push('Keine Daten')
          return
        }

        try {
          const tour = new TechnicianTour(hash, this.distanceCalculator, this.storeCollection, day_ts)
          const bar_data = await tour.barDataReady
          entry.values.push(bar_data.utilizationPercent ?? null)
          entry.titles.push(this.dayTitle(hash))
        } catch (error) {
          console.log(error)
          entry.values.push(null)
          entry.titles.push('Fehler bei der Tourenberechnung')
        }
      }))
    }

    return { start_date: start_monday, technicians: result }
  }

  // WP-Hashes aller Techniker für einen Tag (ein Bulk-Call), gecacht nach id
  private async loadDayHashes(day: Date): Promise<Map<number, WPTechnician>> {
    try {
      const hashes = await firstValueFrom(this.userService.getUsersWPHashes(
        PATimeControl.Instance.dateToDatestring(day, true, false, '-')
      ))
      return new Map(hashes.map(hash => [hash.id, hash]))
    } catch (error) {
      console.log(error)
      return new Map()
    }
  }

  // Tooltip-Text je Tag: Anzahl Aufträge und Auftragszeit (nur die reine
  // Arbeitszeit — die Prozentwerte kommen aus der Tour inkl. Fahrt/Pause)
  private dayTitle(hash: WPTechnician): string {
    const count = hash.day_operations.length
    if (!count) return 'Keine Aufträge'

    const work_minutes = hash.day_operations.reduce((sum, operation) => {
      if (operation.on_site && operation.finished) {
        return sum + Math.max(0, (new Date(operation.finished).getTime() - new Date(operation.on_site).getTime()) / (1000 * 60))
      }
      return sum + (operation.time_estimation ?? 0)
    }, 0)
    const work_hours = (Math.round(work_minutes / 6) / 10).toString().replace('.', ',')
    return `${count} ${count == 1 ? 'Auftrag' : 'Aufträge'} · ${work_hours} h Auftragszeit`
  }

  // Abwesenheitsgrund je Techniker und Tag (Feiertag, Urlaub, keine Schicht)
  // oder null — identische Logik wie bei den Tages-Balken
  private getDayAbsenceReason(technician: GridTechnicianInput, date: Date): string | null {
    const local_day_ts = PATimeControl.Instance.dateToTimestamp(date, true, false)

    // Regionaler öffentlicher Feiertag
    if (technician.country?.toLowerCase().replaceAll(' ', '') == 'deu') {
      const holiday_state = PATimeControl.Instance.germanStatesTranslations[technician.state]
      const public_holidays = PATimeControl.Instance.getTimestampsHolidays(local_day_ts, holiday_state)
        .filter(hd => hd.type == 'public')
      if (public_holidays.length) return public_holidays[0].name
    }

    if (!technician.absence_user_id) return null

    // Eingetragene Abwesenheit
    const date_string = PATimeControl.Instance.timestampToDatestring(local_day_ts, true, '-')
    const absences = this.absenceCalendar.getLoadedUserAbsencesOnDate(technician.absence_user_id, date_string)
    if (absences.length) {
      const reason = this.absenceCalendar.reasonMap.get(absences[0].reasonId)
      return reason?.name ?? 'Abwesend'
    }

    // Keine Schicht an einem Werktag
    const absence_user = this.absenceCalendar.absenceUserMap.get(technician.absence_user_id)
    const weekday = PATimeControl.Instance.getTimeStampsWeekDay(local_day_ts)
    if (
      PATimeControl.Instance.workDays.includes(weekday) && absence_user &&
      !absence_user.hasShiftOnWeekdayWithFallback(weekday, local_day_ts)
    ) {
      return `Keine Schicht am ${PATimeControl.Instance.weekDayToGermanName(weekday, false)}`
    }

    return null
  }
}
